// Aprendizado da IA — tabela de lições: texto, exemplo (recolhido), origem, agente, peso,
// visto por último e chave liga/desliga. Pendente (modo com aprovação) → Aprovar / Recusar.
// PUT /ai-learning/lessons/:id { status: "active" | "disabled" } (o pai chama a API).
import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Switch, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import { Section, StatusBadge, EmptyState } from "../../components/ui";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    hint: { display: "block", marginTop: 2, fontSize: 13, fontWeight: 400, color: t.textSecondary },
    filters: { display: "flex", flexWrap: "wrap", gap: 6 },
    chip: {
      border: `1px solid ${t.line}`,
      background: t.paper,
      color: t.textSecondary,
      borderRadius: 9999,
      padding: "4px 12px",
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      fontFamily: t.font,
      "&:hover": { borderColor: t.lineStrong },
    },
    chipActive: { backgroundColor: t.tint.soft, borderColor: t.tint.strong, color: t.brand, fontWeight: 600 },
    tableWrap: { overflowX: "auto", ...theme.scrollbarStyles },
    table: {
      minWidth: 860,
      "& th": { whiteSpace: "nowrap" },
      "& th:first-child, & td:first-child": { paddingLeft: 20 },
      "& th:last-child, & td:last-child": { paddingRight: 20 },
      "& tbody tr:last-child td": { borderBottom: 0 },
      "& td": { verticalAlign: "top" },
    },
    text: { fontSize: 14, color: t.text, lineHeight: 1.45, maxWidth: 460, whiteSpace: "pre-wrap", wordBreak: "break-word" },
    exampleBtn: {
      marginTop: 4,
      padding: 0,
      border: 0,
      background: "none",
      color: t.brand,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: t.font,
    },
    example: {
      marginTop: 6,
      padding: "8px 10px",
      borderLeft: `3px solid ${t.tint.strong}`,
      backgroundColor: t.muted,
      borderRadius: 4,
      fontSize: 13,
      color: t.textSecondary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      maxWidth: 460,
    },
    muted: { color: t.textMuted, whiteSpace: "nowrap" },
    nowrap: { whiteSpace: "nowrap" },
    actions: { display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" },
  };
});

const STATUS_TONE = { active: "success", pending: "warning", disabled: "neutral", expired: "neutral" };
const FILTERS = ["all", "pending", "active", "disabled", "expired"];

const label = (group, key) => {
  const k = `aiLearning.lessons.${group}.${key}`;
  return i18n.exists(k) ? i18n.t(k) : key || "—";
};

const LessonRow = ({ lesson, onUpdate, classes }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (status) => {
    setBusy(true);
    try {
      await onUpdate(lesson.id, status);
    } finally {
      setBusy(false);
    }
  };
  const pending = lesson.status === "pending";
  return (
    <TableRow data-testid={`lesson-${lesson.id}`}>
      <TableCell>
        <div className={classes.text}>{lesson.text}</div>
        {lesson.example && (
          <>
            <button type="button" className={classes.exampleBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? i18n.t("aiLearning.lessons.hideExample") : i18n.t("aiLearning.lessons.showExample")}
            </button>
            {open && <div className={classes.example}>{lesson.example}</div>}
          </>
        )}
      </TableCell>
      <TableCell className={classes.nowrap}>{label("originLabels", lesson.origin)}</TableCell>
      <TableCell className={classes.nowrap}>{label("agentLabels", lesson.agentKind)}</TableCell>
      <TableCell align="right">{Number(lesson.weight) || 0}</TableCell>
      <TableCell className={classes.muted}>
        {lesson.lastSeenAt ? moment(lesson.lastSeenAt).format("DD/MM/YYYY") : "—"}
      </TableCell>
      <TableCell>
        <StatusBadge color={STATUS_TONE[lesson.status] || "neutral"} label={label("statusLabels", lesson.status)} size="sm" />
      </TableCell>
      <TableCell>
        {pending ? (
          <div className={classes.actions}>
            <Button size="small" variant="contained" color="primary" disabled={busy} onClick={() => run("active")}>
              {i18n.t("aiLearning.lessons.approve")}
            </Button>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => run("disabled")}>
              {i18n.t("aiLearning.lessons.reject")}
            </Button>
          </div>
        ) : (
          <Switch
            color="primary"
            size="small"
            checked={lesson.status === "active"}
            disabled={busy}
            onChange={(e) => run(e.target.checked ? "active" : "disabled")}
            inputProps={{ "aria-label": i18n.t("aiLearning.lessons.toggle") }}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const LessonsTable = ({ lessons, onUpdate }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => {
    const c = { all: lessons.length };
    lessons.forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [lessons]);

  const rows = useMemo(() => {
    const list = filter === "all" ? lessons : lessons.filter((l) => l.status === filter);
    // pendentes primeiro: é o que pede ação
    return [...list].sort((a, b) => (a.status === "pending" ? -1 : 0) - (b.status === "pending" ? -1 : 0));
  }, [lessons, filter]);

  const title = (
    <>
      {i18n.t("aiLearning.lessons.title")}
      <span className={classes.hint}>{i18n.t("aiLearning.lessons.hint")}</span>
    </>
  );

  if (!lessons.length) {
    return (
      <Section title={title} flush>
        <EmptyState
          icon={<EmojiObjectsOutlinedIcon />}
          title={i18n.t("aiLearning.lessons.emptyTitle")}
          message={i18n.t("aiLearning.lessons.emptyMessage")}
        />
      </Section>
    );
  }

  return (
    <Section
      title={title}
      flush
      actions={
        <div className={classes.filters}>
          {FILTERS.filter((f) => f === "all" || counts[f]).map((f) => (
            <button
              key={f}
              type="button"
              className={`${classes.chip} ${filter === f ? classes.chipActive : ""}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
            >
              {label("statusLabels", f)} · {counts[f] || 0}
            </button>
          ))}
        </div>
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon={<EmojiObjectsOutlinedIcon />} title={i18n.t("aiLearning.lessons.filterEmpty")} />
      ) : (
        <div className={classes.tableWrap}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("aiLearning.lessons.text")}</TableCell>
                <TableCell>{i18n.t("aiLearning.lessons.origin")}</TableCell>
                <TableCell>{i18n.t("aiLearning.lessons.agent")}</TableCell>
                <TableCell align="right">{i18n.t("aiLearning.lessons.weight")}</TableCell>
                <TableCell>{i18n.t("aiLearning.lessons.lastSeen")}</TableCell>
                <TableCell>{i18n.t("aiLearning.lessons.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((l) => (
                <LessonRow key={l.id} lesson={l} onUpdate={onUpdate} classes={classes} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Section>
  );
};

export default LessonsTable;
